/**
 * REST API hook for Hyperliquid
 * Handles initial data fetching via REST endpoints
 */

import { useCallback } from 'react';

import { HYPERLIQUID_CONFIG } from '../constants';
import type { PortfolioHistoryTuple } from '../types';
import type { PortfolioHistory } from '../types/portfolio';

export interface UseHyperliquidRestApiParams {
  onPortfolioHistory: (walletId: string, data: PortfolioHistory) => void;
}

export interface UseHyperliquidRestApiReturn {
  fetchInitialPortfolioData: (
    walletId: string,
    address: string
  ) => Promise<void>;
}

/**
 * NOTE: This hook currently only supports portfolio history fetching
 * via the Hyperliquid REST API. It is not a general-purpose REST API
 * interface, but is limited in scope specifically to initial portfolio
 * history retrieval for one or more Hyperliquid wallets.
 */

export function useHyperliquidRestApi({
  onPortfolioHistory,
}: UseHyperliquidRestApiParams): UseHyperliquidRestApiReturn {
  /**
   * Fetch initial portfolio data via REST API for a specific wallet
   */
  const fetchInitialPortfolioData = useCallback(
    async (walletId: string, address: string) => {
      if (!address || !walletId) return;

      try {
        const request = {
          type: 'portfolio',
          user: address,
        };

        const response = await fetch(HYPERLIQUID_CONFIG.API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
        });

        if (!response.ok) {
          return;
        }

        const initialPortfolioData = (await response.json()) as unknown;

        // Normalize into a keyed object for convenience in consumers
        if (Array.isArray(initialPortfolioData)) {
          const keyed: PortfolioHistory = {};
          (initialPortfolioData as PortfolioHistoryTuple[]).forEach((entry) => {
            const [key, series] = entry;
            keyed[key] = series;
          });

          onPortfolioHistory(walletId, keyed);
        }
      } catch (error) {
        console.warn(
          `[HyperLiquid REST V2] Error fetching initial portfolio history for ${walletId}:`,
          error
        );
      }
    },
    [onPortfolioHistory]
  );

  return {
    fetchInitialPortfolioData,
  };
}
